<template>
  <div id="app" v-if="setupFinished">
    <portal-target name="snackbar-destination"></portal-target>
    <portal-target name="modal-destination"></portal-target>
    <pk-header-component :logout="this.isAuthenticated" />
    <transition name="fade" mode="out-in">
      <router-view class="main_view" />
    </transition>
    <pk-footer-component />
    <download-app-banner />
  </div>
</template>

<script>
import { mapActions, mapState, mapMutations } from 'vuex'
import appboy from 'appboy-web-sdk'
import i18n, { localeByCountryCode } from '@/i18n'
import sab from '@/api/sab'
import recaptchaService from '@/js/RecaptchaService'

export default {
  name: 'app',
  data: () => ({ setupFinished: false }),
  methods: {
    ...mapActions({
      install: 'user/install',
      verifyProfile: 'user/verifyProfile',
      refreshToken: 'authentication/refreshToken',
      loadContentTree: 'contentTree/load',
      trackWindowSize: 'window/track',
      signOut: 'authentication/signOut'
    }),
    ...mapMutations({
      setWebViewMode: 'window/SET_WEB_VIEW',
      setUserAgent: 'device/SET_USER_AGENT',
      setCountryCode: 'window/SET_COUNTRY_CODE',
      setLanguage: 'window/SET_LANGUAGE'
    }),
    async setupInternationalization() {
      const countryCode = await sab.getCountryCode()
      this.setCountryCode(countryCode)

      const language = localeByCountryCode[countryCode] || 'en'
      this.setLanguage(language)

      document.querySelector('html').setAttribute('lang', language)
      i18n.locale = language
    },
    async authenticateUser() {
      await this.install()
      await this.refreshToken()
      if (window.location.pathname === '/login/reset') {
        await this.signOut()
        this.$router.replace({
          path: window.location.pathname + window.location.search
        })
        return
      }
      if (this.isAuthenticated) {
        await this.verifyProfile()
        appboy.changeUser(this.accountId)
        appboy.openSession()
      }
    }
  },
  computed: mapState({
    isStoreLoaded: state => state.contentTree.isLoaded,
    isAuthenticated: state => state.authentication.authenticated,
    countryCode: state => state.window.countryCode,
    accountId: state => state.user.accountId
  }),
  async created() {
    window.dataLayer.push({ event: 'optimize.activate' })
    setTimeout(() => {
      window.focus()
    }, 2000)

    this.setWebViewMode(process.env.VUE_APP_CONFIG_ENVIRONMENT.includes('lite'))
    this.setUserAgent(window.navigator.userAgent)
    this.trackWindowSize()

    recaptchaService.init()
    window.remoteConfig.settings.minimumFetchIntervalMillis = 0
    await Promise.all([
      window.remoteConfig.fetchAndActivate(),
      this.setupInternationalization(),
      this.authenticateUser()
    ])

    this.setupFinished = true
    this.loadContentTree(this.countryCode)
  }
}
</script>

<style lang="scss">
@import '~@/scss/main';

*, *::before, *::after {
  box-sizing: border-box;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  -khtml-user-select: none;
  user-select: none;
}

input, input:before, input:after, a {
  -khtml-user-select: initial;
  user-select: initial;
}

html {
  color: #453D4C;
  font-size: 14px;

  @media screen and (max-width: 768px) {
    font-size: 12px;
  }
}

#app {
  // overflow-x: hidden;
  min-height: 100vh;
  font-family: 'Nunito', 'Avenir', Helvetica, Arial, sans-serif;
  // font-weight: 900;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  max-width: 1400px;
  margin: auto;
  box-shadow: 0 0 16px rgba(0,0,0,0.2);
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.main_view {
  flex-grow: 1;
  margin-top: 64px;
}

button, h1, h2, h3 {
  font-family: 'Nunito', 'Avenir', Helvetica, Arial, sans-serif;
  font-weight: 900;
}

a {
  font-weight: bold;
  color: #A217AB;
  text-decoration: underline;
  cursor: pointer;

  &:visited {
    color: #A217AB;
  }
}
</style>
